import { useEffect, useRef, useState } from "react";

type CounterProps = {
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
};

/**
 * Animated counter — starts at 0, eases up to `to` over `duration` ms as soon
 * as the counter enters the viewport. Useful for ROI-style metrics.
 */
export function Counter({
  to,
  prefix = "",
  suffix = "",
  duration = 1400,
}: CounterProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setVal(0);
    let started = false;

    const run = () => {
      started = true;
      const start = performance.now();
      const tick = (t: number) => {
        const k = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - k, 3);
        setVal(Math.round(to * eased));
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !started) {
            run();
            io.unobserve(el);
          }
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref} className="counter">
      {prefix}
      {val}
      {suffix}
    </span>
  );
}
